<template>
  <v-row no-gutters>
    <v-col>
      <div
        id="payment-tracking-list"
        :class="{ 'page-fullscreen': pageStore.isFullScreen }"
      >
        <v-card
          tile
          :class="$vuetify.breakpoint.mdAndDown ? null : 'rounded-t-xl'"
        >
          <PageHeader />

          <v-data-table
            :headers="tableColumns"
            :items="tableItems"
            :loading="tableLoading"
            :height="pageStore.tableHeight"
            loader-height="2"
            :mobile-breakpoint="100"
            fixed-header
            hide-default-footer
            :options.sync="tableOptions"
            :server-items-length="tableTotalRec"
            :disable-items-per-page="true"
            :items-per-page="20"
          >
            <!-- Load More -->
            <template v-slot:body.append v-if="loadMoreLoading">
              <tr>
                <td :colspan="tableColumns.length">
                  <div class="text-center" v-intersect.quiet="loadMore">
                    <v-progress-circular
                      v-if="tableItems.length > 0"
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Arama Alanı -->
            <template v-slot:body.prepend>
              <tr class="data-table-body-search">
                <!-- Belge Tipi -->
                <td class="pa-0">
                  <v-text-field
                    v-model="tableSearchID"
                    :prepend-inner-icon="icons.mdiMagnify"
                    persistent-placeholder
                    clearable
                    placeholder="Ara"
                    filled
                    class="rounded-0 elevation-0"
                    hide-details
                  />
                </td>

                <!-- Belge No -->
                <td class="pa-0">
                  <div class="d-flex">
                    <v-text-field
                      v-model="tableSearchCardNo"
                      :prepend-inner-icon="icons.mdiMagnify"
                      persistent-placeholder
                      clearable
                      placeholder="Ara"
                      filled
                      class="rounded-0 elevation-0"
                      hide-details
                    />
                  </div>
                </td>

                <!-- Cari Unvan -->
                <td class="pa-0">
                  <div class="d-flex">
                    <v-text-field
                      v-model="tableSearchCustomer"
                      :prepend-inner-icon="icons.mdiMagnify"
                      persistent-placeholder
                      clearable
                      placeholder="Ara"
                      filled
                      class="rounded-0 elevation-0"
                      hide-details
                    />
                  </div>
                </td>

                <!-- Boş -->
                <td
                  class="pa-0"
                  v-for="item in tableEmptySearch"
                  :key="`emptySearch-${item}`"
                >
                  <v-text-field
                    filled
                    disabled
                    class="rounded-0 elevation-0"
                    hide-details
                  />
                </td>
              </tr>
            </template>

            <!-- ID -->
            <template #[`item.id`]="{ item }">
              <v-chip label color="chip" class="d-flex justify-center">
                <div
                  class="text-truncate text-uppercase data-table-item-type-one"
                  style="width: 40px !important"
                >
                  {{ item.id }}
                </div>
              </v-chip>
            </template>

            <!-- Kart No -->
            <template #[`item.cc_no`]="{ item }">
              <v-chip color="grey" class="d-flex justify-center" label>
                <div
                  class="text-truncate data-table-item-type-one"
                  style="width: 150px !important"
                >
                  {{ item.cc_no }}
                </div>
              </v-chip>
            </template>

            <!-- Cari Unvan -->
            <template #[`item.customer_name`]="{ item }">
              <div class="d-flex align-center">
                <div>
                  <div
                    class="text-subtitle-2 white--text text-capitalize text-no-wrap"
                  >
                    {{ item.customer_name }}
                  </div>
                </div>
              </div>
            </template>

            <!-- Tutar -->
            <template #[`item.amount`]="{ item }">
              <div
                v-if="item.amount"
                class="text-caption white--text font-weight-medium"
              >
                {{ item.amount | currency({ symbol: "" }) }}
              </div>
            </template>

            <!-- İşlem Tarihi -->
            <template #[`item.datetime`]="{ item }">
              <v-tooltip color="chip" bottom v-if="item.datetime">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    color=""
                    class="d-flex justify-center"
                    label
                    v-bind="attrs"
                    v-on="on"
                  >
                    <div
                      class="text-truncate d-flex align-center data-table-item-type-one"
                      style="width: 150px !important"
                    >
                      <v-icon small class="mr-2">
                        {{ icons.mdiCalendarClock }}
                      </v-icon>
                      <div class="mr-1">
                        {{ item.datetime | moment("DD.MM.YYYY ") }}
                      </div>
                      <span> - {{ item.datetime | moment("HH:mm") }}</span>
                    </div>
                  </v-chip>
                </template>
                <div>
                  {{ item.datetime | moment("DD MMMM dddd") }}
                </div>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from "vue-demi";

// Pinia
import { usePageStore } from "@/stores/pageHeader";
import { useAppStore } from "@/stores/app";

// Utils
import { postData } from "@utils";
import { useDebounceFn } from "@vueuse/core";

// Components
import PageHeader from "@/components/PageHeader/Index.vue";

// Icons
import { mdiCreditCard, mdiMagnify, mdiCalendarClock } from "@mdi/js";

export default {
  components: {
    PageHeader,
  },
  setup() {
    const pageStore = usePageStore();
    const appStore = useAppStore();

    const pageSettings = () => {
      pageStore.pageTitle = "Kredi Kartı Takip";
      pageStore.pageIcon = mdiCreditCard;
      pageStore.hasFullScreen = true;
      pageStore.hasDownload = true;
      pageStore.hasDateRangeAll = false;
      pageStore.hasDateRange = false;
      pageStore.hasActions = false;
    };

    // Table
    const tableLoading = ref(true);
    const tableItems = ref([]);
    const tableTotalRec = ref(0);

    // Table Columns
    const tableColumns = ref([
      {
        text: "ID",
        value: "id",
        sortable: false,
        width: "90px",
      },
      {
        text: "Kart No",
        value: "cc_no",
        sortable: false,
        width: "150px",
      },
      {
        text: "Cari Unvan",
        value: "customer_name",
        sortable: false,
      },
      {
        text: "Tutar (TL)",
        value: "amount",
        align: "right",
        sortable: false,
        width: "150px",
      },
      {
        text: "İşlem Tarihi",
        value: "datetime",
        sortable: false,
        width: "200px",
      },
    ]);

    // Table Load More
    const tablePage = ref(1);
    const loadMoreLoading = ref(false);
    const loadMore = (isIntersecting) => {
      if (isIntersecting) {
        tablePage.value++;
        tableOptions.value.page = tablePage.value;
        if (!tableLoading.value) {
          tableFetch();
        }
      }
    };

    // Tablo Arama

    const tableSearchID = ref("");
    const tableSearchCardNo = ref("");
    const tableSearchCustomer = ref("");
    const tableEmptySearch = ref(2);
    const tableOptions = ref({
      sortBy: ["id"],
      sortDesc: [true],
    });

    const debouncedTableSearch = useDebounceFn(() => {
      tableFetch();
    }, 700);

    watch([tableSearchID, tableSearchCardNo, tableSearchCustomer], () => {
      loadMoreLoading.value = false;
      tableLoading.value = true;
      tablePage.value = 1;
      tableOptions.value.page = 1;
      tableItems.value = [];

      debouncedTableSearch();
    });

    // API İstek
    const tableFetch = () => {
      tableLoading.value = true;

      postData({
        method: "getPaymentActions",
        options: JSON.stringify(tableOptions.value),
        search: JSON.stringify({
          ID: tableSearchID.value,
          card_no: tableSearchCardNo.value,
          customer_name: tableSearchCustomer.value,
        }),
      })
        .then((response) => {
          // API dönüşündeki hatalara bakıyoruz
          if (response.error) {
            throw new Error(response.msg);
          }

          // Toplam kayıt sayısı işleniyor
          tableTotalRec.value = Number(response.response.pagination.totalCount);

          // API'dan gelen veriler işleniyor

          // Responseda bir değer yoksa
          if (response.response.result.length === 0) {
            loadMoreLoading.value = false;
            return;
          }

          // Responsedan gelen veriler işleniyor
          response.response.result.map((item) => {
            tableItems.value.push({ ...item });
          });

          loadMoreLoading.value = false;
          if (tableTotalRec.value >= 20) {
            setTimeout(() => {
              loadMoreLoading.value = true;
            }, 300);
          }
          // Responstan gelen verilere göre loadmore değerlendiriliyor
        })
        .catch((e) => {
          // API hataya girdi

          Vue.swal({
            title: "Hata",
            text: e.message,
            icon: "error",
            width: 300,
            background: "#FF4C51",
            confirmButtonColor: "#A80006",
          });
        })
        .finally(() => {
          // API işlemi bittikten sonra
          tableLoading.value = false;
          startPage.value = false;
        });
    };
    const startPage = ref(false);
    onMounted(() => {
      pageSettings();
      startPage.value = true;
      tableFetch();
    });

    onUnmounted(() => {
      pageStore.pageSettingsClear();
    });

    watch(
      () => [appStore.selectedCustomer],
      (val) => {
        // if (appStore.isCustomer) {
        //   return;
        // }
        if (!startPage.value) {
          loadMoreLoading.value = false;
          tableLoading.value = true;
          tablePage.value = 1;
          tableOptions.value.page = 1;
          tableItems.value = [];
          tableFetch();
        } else {
          startPage.value = false;
        }
      }
    );
    return {
      // Pinia
      pageStore,

      // Table
      tableFetch,
      tableColumns,
      tableItems,
      tableLoading,
      loadMore,
      loadMoreLoading,
      tableTotalRec,
      tableEmptySearch,
      tableOptions,

      // Table Search
      tableSearchID,
      tableSearchCardNo,
      tableSearchCustomer,

      // icons
      icons: {
        mdiCreditCard,
        mdiMagnify,
        mdiCalendarClock,
      },
    };
  },
};
</script>
